/**
 * FriendCardSkeleton Component
 * Skeleton loader matching FriendCardAccepted structure
 */

import React, { useMemo } from 'react';
import { View } from 'react-native';
import { Skeleton, SkeletonAvatar } from '../../ui/Skeleton';
import { useSkeletonCardStyles } from '../../../src/shared/hooks/useSkeletonStyles';
import { friendCardSkeletonStyles, getSkeletonNameWrapperStyle } from './FriendCardSkeleton.styles';
import { getCardDimensions } from './FriendCard.styles';

interface FriendCardSkeletonProps {
  cardWidth?: number;
  /** Test ID for testing */
  testID?: string;
}

export const FriendCardSkeleton: React.FC<FriendCardSkeletonProps> = ({
  cardWidth = 160,
  testID,
}) => {
  // Get dimensions matching real card
  const dimensions = useMemo(() => getCardDimensions(cardWidth), [cardWidth]);

  // Theme-aware card styles
  const { card: cardStyle } = useSkeletonCardStyles({
    borderRadius: 20,
    padding: 0,
    shadow: 'large',
    baseStyle: {
      width: '100%',
      overflow: 'hidden',
      aspectRatio: 0.85,
    },
  });

  // Dynamic styles for dimensions
  const avatarContainerStyle = useMemo(
    () => ({
      ...friendCardSkeletonStyles.avatarContainer,
      marginBottom: dimensions.avatarMarginBottom,
    }),
    [dimensions.avatarMarginBottom]
  );

  const statusDotStyle = useMemo(
    () => ({
      width: dimensions.statusDotSize,
      height: dimensions.statusDotSize,
      borderRadius: dimensions.statusDotSize / 2,
      borderWidth: dimensions.statusDotBorder,
    }),
    [dimensions.statusDotSize, dimensions.statusDotBorder]
  );

  return (
    <View style={cardStyle} testID={testID || 'friend-card-skeleton'}>
      <View style={friendCardSkeletonStyles.cardContent}>
        <View style={avatarContainerStyle}>
          <SkeletonAvatar size={dimensions.avatarSize} />
          <View style={[friendCardSkeletonStyles.statusDotContainer, statusDotStyle]}>
            <Skeleton
              width={dimensions.statusDotSize - dimensions.statusDotBorder * 2}
              height={dimensions.statusDotSize - dimensions.statusDotBorder * 2}
              borderRadius={(dimensions.statusDotSize - dimensions.statusDotBorder * 2) / 2}
            />
          </View>
        </View>
        <View style={getSkeletonNameWrapperStyle(dimensions.nameMarginBottom)}>
          <Skeleton
            width="70%"
            height={dimensions.fontSizeName + 4}
            borderRadius={4}
            testID={testID ? `${testID}-name` : 'friend-card-skeleton-name'}
          />
        </View>
        <Skeleton
          width="50%"
          height={dimensions.fontSizeStat + 4}
          borderRadius={4}
          testID={testID ? `${testID}-stat` : 'friend-card-skeleton-stat'}
        />
      </View>
    </View>
  );
};

